import React from 'react';
import {BaseModal} from '@/components/modals';
import {Icon, IconProps} from '@/components/icons/Icon';
import Button from '@/components/ui/Button';
import {ModalAlertProps} from './types';
import i18n from 'i18n';

const variantConfig = {
    success: {
        icon: 'mdi-check-circle',
        iconColor: '#198754',
        headerClass: 'text-success'
    },
    error: {
        icon: 'mdi-alert-circle',
        iconColor: '#dc3545',
        headerClass: 'text-danger'
    },
    warning: {
        icon: 'mdi-alert',
        iconColor: '#fd7e14',
        headerClass: 'text-warning'
    },
    info: {
        icon: 'mdi-information',
        iconColor: '#0dcaf0',
        headerClass: 'text-info'
    }
};

const ModalAlert: React.FC<ModalAlertProps> = ({
    isOpen,
    onClose,
    variant,
    title,
    message,
    dismissible = true,
    onDismiss,
    className = '',
    icon,
    actions = [],
    size = 'lg',
    backdrop = true
}) => {
    const config = variantConfig[variant];
    const alertIcon = icon || config.icon;

    const handleClose = () => {
        onClose();
        onDismiss?.();
    };

    const defaultTitle = title || `${variant.charAt(0).toUpperCase() + variant.slice(1)}`;

    const footer = actions.length > 0 ? (
        <>
            {actions.map((action, index) => (
                <Button
                    key={index}
                    variant={action.variant || 'primary'}
                    onClick={action.onClick}
                    loading={action.loading}
                    data-cy="alert-0-btn">
                    {action.label}
                </Button>
            ))}
            {dismissible && (
                <Button variant="secondary" onClick={handleClose} data-cy="alert-btn-1">
                    {i18n.t('jt_close.text')}
                </Button>
            )}
        </>
    ) : dismissible ? (
        <Button variant="secondary" onClick={handleClose} data-cy="alert-btn">
            {i18n.t('jt_close.text')}
        </Button>
    ) : undefined;

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={handleClose}
            title={
                <div className={`d-flex align-items-center ${config.headerClass}`}>
                    {alertIcon && (
                        <Icon
                            name={alertIcon as IconProps['name']}
                            className="mr-2 d-flex align-items-center"
                            style={{fontSize: '1.5rem', color: config.iconColor}}
                        />
                    )}
                    {defaultTitle}
                </div>
            }
            size={size}
            backdrop={backdrop}
            className={className}
            footer={footer}
            hideCloseButton={!dismissible}
        >
            <div className="py-2 text-break" style={{whiteSpace: 'pre-line'}}>
                {message}
            </div>
        </BaseModal>
    );
};

export default ModalAlert;
